import type { IReduxState } from 'frontend/configureStore';
import { Alert, Box, Button, FileTypeIcon, Text } from '@ksyos/design-system';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { ConclusieDiagnoseOption } from '../../../../../common/enums/ConclusieDiagnoseOption';
import { getDossierUrl } from '../../../../services/api/dossierService';
import { LoaderSpinner } from '../../atoms/loaderSpinner/LoaderSpinner';
import { CollapseCard } from './CollapseCard';

type ResultCardProps = Readonly<{
    isInitiallyOpen?: boolean;
    result: ConclusieDiagnoseOption;
}>;

export function ResultCard({ isInitiallyOpen, result }: ResultCardProps) {
    const [isLoadingDossier, setIsLoadingDossier] = useState(false);
    const [downloadError, setDownloadError] = useState(false);
    const patientName = useSelector((state: IReduxState) => state.authentication.user?.displayName);

    return (
        <CollapseCard
            isInitiallyOpen={isInitiallyOpen}
            title="Uitslag"
        >
            <>
                {result === ConclusieDiagnoseOption.geen_afwijkingen && (
                    <Text mb={3}>De resultaten van het onderzoek zijn goed.</Text>
                )}
                <Text mb={3}>
                    Hieronder vind je de medische gegevens die bekend zijn bij Ksyos. Download alle informatie in een
                    PDF bestand.
                </Text>
                <Text mb={3}>Je huisarts en optometrist ontvangen ook de uitslag van het onderzoek.</Text>
                <Box
                    alignItems="center"
                    display="flex"
                    flexDirection="column"
                    marginTop={3}
                    paddingY={3}
                >
                    <div data-dd-action-name="File type icon">
                        <FileTypeIcon
                            icon="pdf"
                            onClick={() => downloadDossier()}
                            width={44}
                        />
                    </div>
                    <Text
                        fontStyle="italic"
                        marginTop={4}
                        size="sm"
                    >
                        Medische gegevens {patientName ?? ''}
                    </Text>
                </Box>
            </>
            <div data-dd-action-name="Download button">
                <Button
                    color="secondary"
                    isDisabled={isLoadingDossier}
                    onClick={() => downloadDossier()}
                    variant="solid"
                    width="full"
                >
                    {isLoadingDossier && (
                        <Box paddingRight={2}>
                            <LoaderSpinner />
                        </Box>
                    )}
                    <span>Download gegevens</span>
                </Button>
            </div>
            {downloadError && (
                <ErrorMessage error="Er is een fout opgetreden bij het downloaden van het dossier. Probeer het later nog eens." />
            )}
        </CollapseCard>
    );

    async function downloadDossier() {
        if (isLoadingDossier) {
            return;
        }

        setIsLoadingDossier(true);
        try {
            const dossierUrl = await getDossierUrl();
            window.open(dossierUrl, '_blank');
        } catch (error) {
            console.error('Failed to download dossier', error);
            setDownloadError(true);
        } finally {
            setIsLoadingDossier(false);
        }
    }
}

function ErrorMessage({ error }: Readonly<{ error: string }>) {
    return (
        <Alert
            appearance="danger"
            sx={{
                marginBottom: -3,
                marginTop: 3,
            }}
            withIcon
        >
            <Box
                alignItems="flex-start"
                display="flex"
                flexWrap="wrap"
                gap={4}
                justifyContent="space-between"
                width="100%"
            >
                <Box>
                    <Alert.Text
                        fontWeight="bold"
                        mb={2}
                        size="md"
                    >
                        {error}
                    </Alert.Text>
                </Box>
            </Box>
        </Alert>
    );
}
